import { Component } from '@theme/component';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  async #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    // Source priority (see product-recommendations.liquid):
    //   1. Shopify live recs for the configured intent (complementary) → swap them in.
    //   2. product_data.recommendations metafield → already server-rendered; keep it.
    //   3. Shopify "related" (similarity) recs → fetched here only when neither exists.
    try {
      const result = await this.#fetchCachedRecommendations(productId, sectionId, intent);
      if (!result.success) {
        // If a metafield list is already server-rendered, keep it rather than hiding the
        // section (the recommendations endpoint isn't available under `shopify theme dev`).
        if (this.dataset.hasMf === 'true') {
          this.dataset.recommendationsPerformed = 'true';
          return;
        }
        // The Theme Editor will place a section element in the DOM whose section_id is not available
        // to the Section Renderer API. In this case, we can safely ignore the error.
        if (!Shopify.designMode) {
          this.#handleError(new Error(`Server returned ${result.status}`));
        }
        return;
      }

      const section = this.#parseSection(result.data, id);

      // 1. Live recs for the requested intent → swap them in.
      if (parseInt(section?.dataset.liveCount || '0', 10) > 0) {
        this.#apply(section);
        return;
      }

      // 2. No live recs, but a metafield list is already server-rendered → keep it.
      if (this.dataset.hasMf === 'true') {
        this.dataset.recommendationsPerformed = 'true';
        return;
      }

      // 3. Last resort: fetch Shopify "related" (similarity) recs.
      if (intent !== 'related') {
        const related = await this.#fetchCachedRecommendations(productId, sectionId, 'related');
        if (related.success) {
          const relatedSection = this.#parseSection(related.data, id);
          if (relatedSection?.innerHTML && relatedSection.innerHTML.trim().length) {
            this.#apply(relatedSection);
            return;
          }
        }
      }

      this.#handleError(new Error('No recommendations available'));
    } catch (e) {
      // Network/abort error — keep a server-rendered metafield list if we have one.
      if (this.dataset.hasMf === 'true') {
        this.dataset.recommendationsPerformed = 'true';
        return;
      }
      this.#handleError(e);
    }
  }

  /**
   * Parse a fetched section response into its <product-recommendations> element.
   * @param {string} data
   * @param {string} id
   * @returns {Element | null}
   */
  #parseSection(data, id) {
    const html = document.createElement('div');
    html.innerHTML = data || '';
    return html.querySelector(`product-recommendations[id="${id}"]`);
  }

  /**
   * Swap in a fetched section's contents.
   * @param {Element} section
   */
  #apply(section) {
    this.dataset.recommendationsPerformed = 'true';
    requestAnimationFrame(() => {
      this.innerHTML = section.innerHTML;
    });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
